/*
 * subscription-selector.js
 * ---------------------------------------------------------------------------
 * Custom Subscribe-&-Save vs one-time selector for the Bamboo TP PDP, built on
 * native Shopify selling plans (Recharge registers the plans on the backend).
 *
 * The <subscription-selector> element owns three controls — roll count (variant),
 * purchase type (subscribe / one-time), and cadence (selling plan) — and writes
 * the resulting `id` + `selling_plan` into the buy_buttons <form> so the theme's
 * existing product-form.js AJAX add-to-cart posts them unchanged.
 *
 * Defaults: Subscribe · every 2 months · first available roll count.
 * ---------------------------------------------------------------------------
 */
if (!customElements.get('subscription-selector')) {
  customElements.define('subscription-selector', class SubscriptionSelector extends HTMLElement {
    connectedCallback() {
      if (this._init) return; // guard against re-entry on section hot-reload
      this._init = true;

      const dataEl = this.querySelector('.subscription-selector__data');
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.data = { variants: [] };
      }

      this.formId = this.dataset.form;
      this.sectionId = this.dataset.section;
      this.variantId = this.dataset.defaultVariant;
      this.purchase = 'subscribe';

      this.rollButtons = Array.from(this.querySelectorAll('.sub-roll'));
      this.cadencePills = Array.from(this.querySelectorAll('.subscription-selector__cadence-pill'));
      this.purchaseInputs = Array.from(this.querySelectorAll('input[name^="purchase-type"]'));
      this.subscribeLabel = this.querySelector('[data-purchase="subscribe"]');
      this.onetimeLabel = this.querySelector('[data-purchase="onetime"]');

      // Dropdown cadence style (block setting "cadence_style"). Both controls are in the
      // DOM at once — CSS shows the native <select> ≤1024px and the listbox ≥1025px — so
      // there is no runtime mode switch; render() just keeps both pointed at this.planId.
      // In pill mode these are all null/empty and every branch below no-ops.
      this.cadenceSelect = this.querySelector('.sub-cadence__native');
      this.cadenceTrigger = this.querySelector('.sub-cadence__trigger');
      this.cadenceList = this.querySelector('.sub-cadence__list');
      // The Subscribe card clips its children (overflow: hidden for its radius), which
      // would cut the open panel off. We drop that clipping only while open — see
      // .cadence-open in section-main-product.css. Null on the trial selector, whose
      // cadence group sits outside any card.
      this.cadenceClipper = this.cadenceList ? this.cadenceList.closest('.subscription-selector__option') : null;
      this.cadenceOptions = Array.from(this.querySelectorAll('.sub-cadence__option'));
      this.cadenceFocusIndex = 0;

      // Default plan = the cadence pill pre-marked active (every 2 months), falling back
      // to the first pill if that cadence isn't offered — or, in dropdown mode, to the
      // <select>'s pre-selected option.
      const activePill = this.querySelector('.subscription-selector__cadence-pill.is-active') || this.cadencePills[0];
      this.planId = activePill ? activePill.dataset.planId : (this.cadenceSelect ? this.cadenceSelect.value : null);

      this.bind();
      this.render();

      // The buy_buttons <form> is parsed after this element, so it may not exist
      // yet at connect time. Sync now (no-op if absent) and again once the DOM
      // is ready, so the default variant + plan are on the form before any input.
      this.syncForm();
      this.bindFormSubmit();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => { this.syncForm(); this.bindFormSubmit(); }, { once: true });
      }

      // Recharge's storefront embed auto-injects its stock widget into the product
      // form — including its own `selling_plan` input, which it re-adds reactively
      // (on form change / submit) and would fight ours. Removing the whole widget
      // subtree deletes that logic at the source and it does NOT re-mount. It mounts
      // asynchronously, so also watch for it via a MutationObserver on the product
      // info column and remove it whenever it (re)appears. Don't instead strip its
      // input at submit — that races Recharge's own handlers and fails intermittently —
      // and don't disable the Recharge app embed globally: other products use its widget.
      this.killRecharge();
      const observeTarget = this.closest('[id^="ProductInfo-"]') || document.body;
      this.rechargeObserver = new MutationObserver(() => this.killRecharge());
      this.rechargeObserver.observe(observeTarget, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      if (this.rechargeObserver) this.rechargeObserver.disconnect();
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    }

    killRecharge() {
      document.querySelectorAll('.rc-container-wrapper').forEach((el) => el.remove());
    }

    bind() {
      this.rollButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          this.variantId = btn.dataset.variantId;
          this.render();
          this.syncForm();
          this.notifyVariantChange();
        });
      });

      this.cadencePills.forEach((pill) => {
        pill.addEventListener('click', () => this.selectPlan(pill.dataset.planId));
      });

      if (this.cadenceSelect) {
        this.cadenceSelect.addEventListener('change', () => this.selectPlan(this.cadenceSelect.value));
      }
      this.bindCadenceListbox();

      this.purchaseInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.purchase = input.value;
          this.render();
          this.syncForm();
        });
      });
    }

    // Every cadence control (pill, native <select>, listbox option) lands here. Picking a
    // cadence implies subscribing, so it also flips the purchase radio back — matching the
    // long-standing pill behaviour. On the trial selector there are no radios and
    // this.purchase is already 'subscribe', so that part is a no-op.
    selectPlan(planId) {
      if (!planId) return;
      this.planId = planId;
      this.purchase = 'subscribe';
      const subInput = this.purchaseInputs.find((i) => i.value === 'subscribe');
      if (subInput) subInput.checked = true;
      this.render();
      this.syncForm();
    }

    // Desktop listbox (WAI-ARIA collapsed-listbox pattern): the trigger owns focus while
    // closed, the <ul> takes it while open and tracks the active option with
    // aria-activedescendant. Mobile never reaches any of this — the native <select> is
    // what's visible there, and it needs no scripting beyond its 'change' event.
    bindCadenceListbox() {
      if (!this.cadenceTrigger || !this.cadenceList) return;

      this.cadenceTrigger.addEventListener('click', () => {
        if (this.cadenceList.hidden) this.openCadenceList();
        else this.closeCadenceList(true);
      });

      this.cadenceOptions.forEach((opt) => {
        opt.addEventListener('click', () => {
          this.selectPlan(opt.dataset.planId);
          this.closeCadenceList(true);
        });
      });

      const onKeydown = (event) => this.onCadenceKeydown(event);
      this.cadenceTrigger.addEventListener('keydown', onKeydown);
      this.cadenceList.addEventListener('keydown', onKeydown);

      this.onDocumentClick = (event) => {
        if (!this.cadenceList.hidden && !this.contains(event.target)) this.closeCadenceList();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    onCadenceKeydown(event) {
      const open = !this.cadenceList.hidden;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (open) this.setCadenceFocus(this.cadenceFocusIndex + 1); else this.openCadenceList();
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (open) this.setCadenceFocus(this.cadenceFocusIndex - 1); else this.openCadenceList();
          break;
        case 'Home':
          if (open) { event.preventDefault(); this.setCadenceFocus(0); }
          break;
        case 'End':
          if (open) { event.preventDefault(); this.setCadenceFocus(this.cadenceOptions.length - 1); }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault(); // also suppresses the button's synthesised click
          if (open) {
            const opt = this.cadenceOptions[this.cadenceFocusIndex];
            if (opt) this.selectPlan(opt.dataset.planId);
            this.closeCadenceList(true);
          } else {
            this.openCadenceList();
          }
          break;
        case 'Escape':
          if (open) { event.preventDefault(); this.closeCadenceList(true); }
          break;
        case 'Tab':
          this.closeCadenceList();
          break;
        default:
          break;
      }
    }

    openCadenceList() {
      if (!this.cadenceList || !this.cadenceList.hidden) return;
      this.cadenceList.hidden = false;
      if (this.cadenceClipper) this.cadenceClipper.classList.add('cadence-open');
      this.cadenceTrigger.setAttribute('aria-expanded', 'true');
      const i = this.cadenceOptions.findIndex((o) => String(o.dataset.planId) === String(this.planId));
      this.setCadenceFocus(i < 0 ? 0 : i);
      // preventScroll: the Subscribe card is a clipping/scrolling ancestor, and a plain
      // focus() makes the browser scroll IT to reveal the list — which slides the card's
      // own title/price/benefits out of view. The CSS drops that clipping while open.
      this.cadenceList.focus({ preventScroll: true });
    }

    closeCadenceList(refocus) {
      if (!this.cadenceList || this.cadenceList.hidden) return;
      this.cadenceList.hidden = true;
      if (this.cadenceClipper) this.cadenceClipper.classList.remove('cadence-open');
      this.cadenceTrigger.setAttribute('aria-expanded', 'false');
      this.cadenceList.removeAttribute('aria-activedescendant');
      this.cadenceOptions.forEach((o) => o.classList.remove('is-focused'));
      if (refocus) this.cadenceTrigger.focus();
    }

    setCadenceFocus(index) {
      if (!this.cadenceOptions.length) return;
      const last = this.cadenceOptions.length - 1;
      this.cadenceFocusIndex = Math.min(Math.max(index, 0), last);
      const opt = this.cadenceOptions[this.cadenceFocusIndex];
      this.cadenceOptions.forEach((o, i) => o.classList.toggle('is-focused', i === this.cadenceFocusIndex));
      this.cadenceList.setAttribute('aria-activedescendant', opt.id);

      // Keep the active option in view by scrolling the LIST's own box only. scrollIntoView
      // would walk every scrollable ancestor — including the Subscribe card — and drag the
      // card's contents out of its clip box (same trap as focus() above).
      const list = this.cadenceList;
      const optTop = opt.offsetTop;
      const optBottom = optTop + opt.offsetHeight;
      if (optTop < list.scrollTop) list.scrollTop = optTop;
      else if (optBottom > list.scrollTop + list.clientHeight) list.scrollTop = optBottom - list.clientHeight;
    }

    variant() {
      return this.data.variants.find((v) => String(v.id) === String(this.variantId)) || this.data.variants[0];
    }

    // Plans can be assigned per variant (Recharge "Manage variant plans"), so the pressed
    // pill's plan id may belong to another variant. Map it to the current variant's plan
    // with the same cadence ("number:unit", from data.cadence). Product-wide plans, or a
    // selector without that data (the paid selector), resolve to the same id — a no-op.
    resolvePlan(planId) {
      if (!planId) return planId;
      const v = this.variant();
      const cadence = this.data.cadence;
      if (!v || !Array.isArray(v.plans) || !cadence) return planId;
      if (v.plans.some((p) => String(p) === String(planId))) return planId;
      const key = cadence[String(planId)];
      if (!key) return planId;
      const match = v.plans.find((p) => cadence[String(p)] === key);
      return match ? String(match) : planId;
    }

    render() {
      const v = this.variant();
      const subscribing = this.purchase === 'subscribe';

      this.rollButtons.forEach((b) => {
        const on = String(b.dataset.variantId) === String(this.variantId);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (v) {
        const subEl = this.querySelector('[data-role="price-subscribe"]');
        const oneEl = this.querySelector('[data-role="price-onetime"]');
        const cmpEl = this.querySelector('[data-role="price-compare"]');
        // Subscribe price for the currently-selected cadence (plan); falls back to the
        // one-time price if this variant has no allocation for that plan.
        const subPrice = (v.subByPlan && this.planId && v.subByPlan[this.planId]) || v.oneTime;
        if (subEl) subEl.textContent = subPrice;
        if (oneEl) oneEl.textContent = v.oneTime;
        // Strikethrough compare price on the Subscribe row — only when it saves.
        if (cmpEl) {
          const saves = subPrice !== v.oneTime;
          cmpEl.textContent = saves ? v.oneTime : '';
          cmpEl.hidden = !saves;
        }
      }

      if (this.subscribeLabel) this.subscribeLabel.classList.toggle('is-active', subscribing);
      if (this.onetimeLabel) this.onetimeLabel.classList.toggle('is-active', !subscribing);
      this.classList.toggle('is-subscribe', subscribing);
      this.classList.toggle('is-onetime', !subscribing);
      // The cadence row stays mounted in both modes — one-time used to hide it, which made the
      // pills vanish and reappear as the shopper toggled back and forth. Clicking a cadence
      // flips the purchase radio back to subscribe (see selectPlan), so leaving it live also
      // gives one-time shoppers a one-click way back into the subscription.

      // One-time clears the pill highlight — nothing is being delivered on a cadence, so no
      // pill reads as pressed. this.planId is kept, so toggling back to Subscribe restores the
      // shopper's cadence (and the Subscribe card keeps pricing that plan meanwhile).
      this.cadencePills.forEach((p) => {
        const on = subscribing && String(p.dataset.planId) === String(this.planId);
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      // Dropdown style: point the native <select> and the desktop listbox at the same plan,
      // and copy the selected option's text up into the trigger. Both are kept current even
      // though only one is visible, so a resize across 1025px needs no re-render.
      if (this.cadenceSelect && this.planId && this.cadenceSelect.value !== String(this.planId)) {
        this.cadenceSelect.value = String(this.planId);
      }
      let activeOption = null;
      this.cadenceOptions.forEach((o) => {
        const on = String(o.dataset.planId) === String(this.planId);
        if (on) activeOption = o;
        o.classList.toggle('is-active', on);
        o.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (this.cadenceTrigger && activeOption) {
        const valueEl = this.cadenceTrigger.querySelector('.sub-cadence__value-label');
        const subEl = this.cadenceTrigger.querySelector('.sub-cadence__value-sub');
        const srcLabel = activeOption.querySelector('.sub-cadence__option-label');
        const srcSub = activeOption.querySelector('.sub-cadence__option-sub');
        if (valueEl && srcLabel) valueEl.textContent = srcLabel.textContent;
        if (subEl) {
          subEl.textContent = srcSub ? srcSub.textContent : '';
          subEl.hidden = !srcSub;
        }
      }
      // Switching to one-time isn't a cadence choice — don't leave an open menu behind.
      if (!subscribing) this.closeCadenceList();

      this.renderSubmitLabel(subscribing);
      this.renderSubscribeSubtext(subscribing);
    }

    // The Subscribe & Save subtext under the Add-to-cart button (buy_buttons block) is
    // shown only while subscribing. Like the sticky-bar label in renderSubmitLabel, it
    // lives outside this element and is matched by its data-form back-reference; a blank
    // setting renders no element at all, so the query simply finds nothing.
    renderSubscribeSubtext(subscribing) {
      document
        .querySelectorAll(`[data-role="subscribe-subtext"][data-form="${this.formId}"]`)
        .forEach((el) => { el.hidden = !subscribing; });
    }

    // The Add-to-cart label follows the purchase mode ("Add to cart" ↔ "Subscribe"), so the
    // button states what the click will actually do. Two buttons carry the label: the in-page
    // one in the buy_buttons block — a sibling of this element, not a descendant, so it is
    // reached through the form — and the mobile sticky bar, which sits outside the form
    // entirely and is matched by its data-form back-reference. Both use the same span and the
    // same data attributes so they can't drift. Opt-in: main-product.liquid only emits the
    // data attributes when the merchant sets a Subscribe label AND the variant is purchasable,
    // so a sold-out button keeps its "Sold out" text.
    renderSubmitLabel(subscribing) {
      const form = document.getElementById(this.formId);
      const labels = [];
      const inPage = form && form.querySelector('.product-form__submit [data-role="atc-label"]');
      if (inPage) labels.push(inPage);
      document
        .querySelectorAll(`.wythout-sticky-atc[data-form="${this.formId}"] [data-role="atc-label"]`)
        .forEach((el) => labels.push(el));

      labels.forEach((label) => {
        if (!label.dataset.labelSubscribe) return;
        const next = subscribing ? label.dataset.labelSubscribe : label.dataset.labelOnetime;
        if (next && label.textContent.trim() !== next) label.textContent = next;
      });
    }

    // syncForm() strips foreign selling_plan inputs on connect + on every change, but a late
    // Recharge injection could land AFTER the shopper's last interaction — and a submit
    // listener can't help: product-form.js registers its handler first on the same form
    // node, so it serializes the FormData before any later listener runs. The `formdata`
    // event is the reliable hook: it fires synchronously INSIDE `new FormData(form)`, and
    // mutating event.formData rewrites the payload that actually posts, regardless of
    // listener order or what Recharge injected into the DOM. Because of that, the hidden
    // input's value can disagree with what posts — QA a cadence via new FormData(form)
    // or /cart.js, never the input.
    bindFormSubmit() {
      const form = document.getElementById(this.formId);
      if (!form || this._submitBound) return;
      this._submitBound = true;
      form.addEventListener('formdata', (event) => {
        const fd = event.formData;
        fd.set('id', this.variantId);
        if (this.purchase === 'subscribe' && this.planId) {
          fd.set('selling_plan', this.resolvePlan(this.planId));
        } else {
          fd.delete('selling_plan');
        }
      });
    }

    // The parts of the theme's variant-change pipeline the disabled variant_picker
    // used to drive: shareable ?variant= URL, pickup availability, the variant's
    // featured image, and the pub-sub event. `html` is omitted from the payload —
    // the only theme subscriber (recipient-form) reads sectionId + variant.id.
    notifyVariantChange() {
      const v = this.variant();
      if (!v) return;

      try {
        window.history.replaceState({}, '', `${window.location.pathname}?variant=${v.id}`);
      } catch (e) { /* history API blocked (e.g. sandboxed preview) — non-fatal */ }

      const pickup = document.querySelector('pickup-availability');
      if (pickup && typeof pickup.fetchAvailability === 'function') {
        pickup.dataset.variantId = v.id;
        pickup.fetchAvailability(v.id);
      }

      if (v.media && this.sectionId) {
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(`${this.sectionId}-${v.media}`, true);
          if (typeof gallery.syncTakeoverState === 'function') gallery.syncTakeoverState();
        }
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, variant: v },
        });
      }
    }

    syncForm() {
      const form = document.getElementById(this.formId);
      if (!form) return;

      const idInput = form.querySelector('[name="id"]');
      if (idInput) {
        idInput.value = this.variantId;
        idInput.disabled = false;
      }

      // Strip any foreign selling_plan inputs (Recharge's auto-injected widget)
      // so ours is the only one posted.
      form.querySelectorAll('input[name="selling_plan"]:not([data-wythout-sp])').forEach((el) => el.remove());

      let planInput = form.querySelector('input[name="selling_plan"][data-wythout-sp]');
      if (this.purchase === 'subscribe' && this.planId) {
        if (!planInput) {
          planInput = document.createElement('input');
          planInput.type = 'hidden';
          planInput.name = 'selling_plan';
          planInput.setAttribute('data-wythout-sp', '');
          form.appendChild(planInput);
        }
        planInput.value = this.resolvePlan(this.planId);
        planInput.disabled = false;
      } else if (planInput) {
        planInput.remove();
      }
    }
  });
}
